import React, { useEffect, useMemo, useState } from "react";
import { Card, CardBody, CardHeader, Toggle, FormField, PrimaryButton, SecondaryButton } from "../../design-system";
import { borders, surfaces, textColors, typography } from "../../design-system/tokens";
import { useAdminSubscriptionConfig, useUpsertAdminSubscriptionConfig } from "../hooks/useAdminSubscriptionConfig";

const noticeStyles = {
  success: "bg-green-100 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-300",
  error: "bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300",
};

const SubscriptionConfigCard = ({ className = "", isMobile = false }) => {
  const { data, isLoading, error } = useAdminSubscriptionConfig();
  const upsertMutation = useUpsertAdminSubscriptionConfig();

  const [notice, setNotice] = useState(null);

  const initial = useMemo(() => {
    const cfg = data || null;
    return {
      // Product rule: if no config exists, default is allowed=true.
      new_subscriptions_allowed: cfg?.new_subscriptions_allowed ?? true,
    };
  }, [data]);

  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);

  const onSave = async () => {
    setNotice(null);
    try {
      await upsertMutation.mutateAsync({
        new_subscriptions_allowed: Boolean(form.new_subscriptions_allowed),
      });
      setNotice({ type: "success", message: "Saved subscription configuration." });
    } catch (e) {
      setNotice({ type: "error", message: e?.message || "Failed to save." });
    }
  };

  return (
    <Card className={className}>
      <CardHeader title="Subscription Configuration" />
      <CardBody>
        <div className={isMobile ? "space-y-4" : "flex items-start justify-between gap-4"}>
          <div>
            <div className={`${typography.bodySmall} ${textColors.secondary}`}>
              Control whether users can start or upgrade paid subscriptions. When disabled, upgrade attempts should be captured as leads instead of charging.
            </div>
          </div>
          <div className={`flex ${isMobile ? "flex-col w-full" : "items-center"} gap-2`}>
            <SecondaryButton
              onClick={() => setForm(initial)}
              disabled={isLoading || upsertMutation.isPending}
              className={isMobile ? "w-full" : ""}
            >
              Reset
            </SecondaryButton>
            <PrimaryButton
              onClick={onSave}
              disabled={isLoading || upsertMutation.isPending}
              className={isMobile ? "w-full" : ""}
            >
              Save
            </PrimaryButton>
          </div>
        </div>

        {notice ? (
          <div className="mt-4">
            <div className={`rounded-md px-4 py-3 ${noticeStyles[notice.type] || `${surfaces.glass} ${borders.glass} border ${textColors.secondary}`}`}>
              <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{notice.message}</p>
            </div>
          </div>
        ) : null}

        {error ? (
          <div className="mt-4">
            <div className={`rounded-md px-4 py-3 ${noticeStyles.error}`}>
              <p className={`${typography.bodySmall} whitespace-pre-wrap`}>{error?.message || "Failed to load."}</p>
            </div>
          </div>
        ) : null}

        <div className="mt-6">
          <FormField label="Allow new subscriptions / upgrades">
            <Toggle
              checked={Boolean(form.new_subscriptions_allowed)}
              onChange={(e) => setForm((p) => ({ ...p, new_subscriptions_allowed: e.target.checked }))}
              label={form.new_subscriptions_allowed ? "Enabled" : "Disabled"}
              labelPosition="right"
            />
          </FormField>

          <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
            Default behavior (when no config exists): enabled.
          </div>
        </div>
      </CardBody>
    </Card>
  );
};

export default SubscriptionConfigCard;

